import React, { useEffect, useState, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaQuoteLeft,
  FaArrowRight,
  FaArrowLeft,
  FaPlay,
  FaPause,
  FaStar,
} from "react-icons/fa";
import WaveSurfer from "wavesurfer.js";
import testlogo from "../Assets/Indorinama-website-elements/Icons/testimonial.svg";
import axios from "../lib/axios";

const TestimonialCard = ({
  testimonial,
}) => {
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    let wavesurfer = null;
    let isSubscribed = true;

    const initWaveSurfer = async () => {
      if (
        testimonial.audio?.url &&
        waveformRef.current &&
        !wavesurferRef.current
      ) {
        setIsLoading(true);
        try {
          wavesurfer = WaveSurfer.create({
            container: waveformRef.current,
            waveColor: "black",
            progressColor: "#bc1535",
            cursorColor: "#bc1535",
            barWidth: 2,
            barGap: 3,
            height: 40,
            responsive: true,
            barRadius: 3,
            cursorWidth: 2,
            interact: true,
            backend: "MediaElement",
            mediaControls: false,
            normalize: true,
            xhr: {
              cache: true,
              mode: "cors",
              method: "GET",
              credentials: "same-origin",
              headers: [
                { key: "cache-control", value: "public, max-age=31536000" },
              ],
            },
          });

          wavesurferRef.current = wavesurfer;

          wavesurfer.on("loading", function (progress) {
            if (isSubscribed) {
              setIsLoading(progress < 100);
            }
          });

          wavesurfer.on("ready", function () {
            if (isSubscribed) {
              setIsLoading(false);
            }
          });

          wavesurfer.on("play", function () {
            if (isSubscribed) {
              setIsPlaying(true);
            }
          });

          wavesurfer.on("pause", function () {
            if (isSubscribed) {
              setIsPlaying(false);
            }
          });

          wavesurfer.on("finish", function () {
            if (isSubscribed) {
              setIsPlaying(false);
              wavesurfer.seekTo(0);
            }
          });

          await wavesurfer.load(testimonial.audio?.cloudinary ? testimonial.audio.cloudinary.secure_url : testimonial.audio.url);
        } catch (error) {
          console.error("Error initializing WaveSurfer:", error);
        } finally {
          if (isSubscribed) {
            setIsLoading(false);
          }
        }
      }
    };

    initWaveSurfer();

    return () => {
      isSubscribed = false;
      if (wavesurferRef.current) {
        try {
          wavesurferRef.current.pause();
          wavesurferRef.current.destroy();
          wavesurferRef.current = null;
        } catch (error) {
          console.error("Error destroying WaveSurfer:", error);
        }
      }
    };
  }, [testimonial.audio]);

  const handleAudioToggle = () => {
    if (isLoading || !wavesurferRef.current) {
      return;
    }

    wavesurferRef.current.playPause();
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      // This make the animation slower by a bit
      // exit={{ opacity: 0, x: -50 }}
      className="bg-white rounded-2xl shadow-xl p-6 md:p-8 flex flex-col h-full"
    >
      <div className="relative group mb-6">
        <div className="flex items-center gap-4">
          <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-brand-black flex-shrink-0">
            <img
              src={testimonial?.image?.cloudinary ? testimonial.image.cloudinary.secure_url : testimonial.image?.url}
              alt={testimonial?.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex flex-col">
            <h3 className="text-xl font-bold text-brand-red">
              {testimonial?.name}
            </h3>
            <p className="text-brand-black font-medium">
              {testimonial?.company_name}
            </p>
            <div className="mt-2 flex items-center gap-1 text-[#bc1535]">
              {Array.from({ length: 5 }).map((_, index) => (
                <FaStar
                  key={`${testimonial?.name || "testimonial"}-star-${index}`}
                  className={`text-sm ${
                    index < Number(testimonial?.rating || 0)
                      ? "opacity-100"
                      : "opacity-20"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="absolute top-0 right-0">
          <FaQuoteLeft className="text-3xl text-[#bc1535]/20 mb-4" />
        </div>
      </div>

      <div className="flex-1">
        <p className="text-gray-600 text-base leading-relaxed mb-4">
          {testimonial.description}
        </p>
      </div>

      {testimonial.audio && (
        <div className="relative">
          <div className="flex items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.1 }}
              onClick={handleAudioToggle}
              className={`w-10 h-10 bg-brand-black rounded-full flex items-center justify-center shadow-lg text-white flex-shrink-0 ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : isPlaying ? (
                <FaPause />
              ) : (
                <FaPlay />
              )}
            </motion.button>
            <div
              ref={waveformRef}
              className="flex-grow rounded-lg overflow-hidden bg-gray-50 p-2"
            />
          </div>
        </div>
      )}
    </motion.div>
  );
};

const Clients = ({ category }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(3);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  useEffect(() => {
    const updateItemsPerPage = () => {
      if (window.innerWidth < 640) {
        setItemsPerPage(1);
        return;
      }

      if (window.innerWidth < 1024) {
        setItemsPerPage(2);
        return;
      }

      setItemsPerPage(3);
    };

    updateItemsPerPage();
    window.addEventListener("resize", updateItemsPerPage);

    return () => window.removeEventListener("resize", updateItemsPerPage);
  }, []);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(testimonials.length / itemsPerPage));
    if (currentPage > totalPages - 1) {
      setCurrentPage(0);
    }
  }, [currentPage, itemsPerPage, testimonials.length]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/globals/testimonials');
        
        // Get the testimonials for the current category
        const categoryKey = `${category}`;
        const categoryTestimonials = Array.isArray(response.data[categoryKey])
          ? response.data[categoryKey]
          : [];
        const sortedTestimonials = [...categoryTestimonials].sort((a, b) => {
          const sortA = Number(a?.sort_order ?? 0);
          const sortB = Number(b?.sort_order ?? 0);

          if (sortA !== sortB) {
            return sortA - sortB;
          }

          return String(a?.name || "").localeCompare(String(b?.name || ""));
        });
        
        setTestimonials(sortedTestimonials);
        setError(null);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        setError('Failed to load testimonials');
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, [category]);

  const handleNext = () => {
    setCurrentPage((prev) =>
      prev >= Math.ceil(testimonials.length / itemsPerPage) - 1 ? 0 : prev + 1
    );
  };

  const handlePrev = () => {
    setCurrentPage((prev) =>
      prev === 0 ? Math.ceil(testimonials.length / itemsPerPage) - 1 : prev - 1
    );
  };

  const handleTouchStart = (e) => {
    const touch = e.changedTouches?.[0];
    if (!touch) return;
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current == null || touchStartY.current == null) return;

    const touch = e.changedTouches?.[0];
    if (!touch) return;

    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;

    // Only treat mostly-horizontal gestures as swipes
    if (Math.abs(deltaX) < 50 || Math.abs(deltaX) < Math.abs(deltaY)) return;

    if (deltaX < 0) {
      handleNext();
    } else {
      handlePrev();
    }
  };

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!testimonials.length) return null;

  const currentTestimonials = testimonials.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  return (
    <div className="relative overflow-hidden">
      <div className="mb-8 text-center sm:mb-12">
        <motion.img
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          src={testlogo}
          alt="Testimonials"
          className="mx-auto max-md:h-[3.75rem]"
          style={{ maxHeight: "5rem" }}
        />
      </div>

      <div className="relative mx-auto max-w-7xl px-4">
        <div
          className="relative touch-pan-y"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="wait">
              {currentTestimonials.map((testimonial) => (
                <TestimonialCard
                  key={testimonial.id}
                  testimonial={testimonial}
                />
              ))}
            </AnimatePresence>
          </div>

          {/* Navigation Arrows — centered on the cards row */}
          {testimonials.length > itemsPerPage && (
            <div className="mt-6 flex items-center justify-between gap-3 sm:absolute sm:inset-x-0 sm:top-1/2 sm:mt-0 sm:-translate-y-1/2 sm:pointer-events-none">
              <motion.button
                whileHover={{ scale: 1.1 }}
                onClick={handlePrev}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-black text-white shadow-lg transition-colors sm:pointer-events-auto"
                aria-label="Previous testimonials"
              >
                <FaArrowLeft className="text-lg" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                onClick={handleNext}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-black text-white shadow-lg transition-colors sm:pointer-events-auto"
                aria-label="Next testimonials"
              >
                <FaArrowRight className="text-lg" />
              </motion.button>
            </div>
          )}
        </div>

        {/* Testimonial Indicators */}
        {testimonials.length > itemsPerPage && (
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({
              length: Math.ceil(testimonials.length / itemsPerPage),
            }).map((_, index) => (
              <motion.button
                key={index}
                whileHover={{ scale: 1.2 }}
                onClick={() => setCurrentPage(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentPage ? "w-6 bg-[#bc1535]" : "bg-gray-300"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Clients;
